/**
 * Authentication Service
 *
 * Provides authentication methods that work with AWS Cognito via Amplify.
 * Currently uses mock implementation for local development.
 * Switch to real Amplify by setting USE_MOCK_AUTH=false in env.
 */

// Types
export interface User {
  id: string;
  username: string;
  email: string;
  emailVerified: boolean;
  createdAt: string;
  tier?: "free" | "premium";
}

export interface SignUpInput {
  username: string;
  email: string;
  password: string;
}

export interface SignInInput {
  email: string;
  password: string;
}

export interface ConfirmSignUpInput {
  email: string;
  code: string;
}

export interface ResetPasswordInput {
  email: string;
}

export interface ConfirmResetPasswordInput {
  email: string;
  code: string;
  newPassword: string;
}

export interface AuthResult {
  success: boolean;
  user?: User;
  error?: string;
  needsConfirmation?: boolean;
}

// Error types
export class AuthError extends Error {
  constructor(
    message: string,
    public code: string
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

// Mock user storage (for local development)
const mockUsers: Map<
  string,
  { user: User; password: string; confirmed: boolean }
> = new Map();
let currentUser: User | null = null;

// Check if we should use mock implementation
const USE_MOCK = import.meta.env.VITE_USE_MOCK_AUTH !== 'false';

/**
 * Sign up a new user
 */
export async function signUp(input: SignUpInput): Promise<AuthResult> {
  if (USE_MOCK) {
    return mockSignUp(input);
  }

  // TODO: Real Amplify implementation
  // const { signUp } = await import('aws-amplify/auth');
  // return signUp({ username: input.email, password: input.password, options: { userAttributes: { email: input.email, preferred_username: input.username } } });
  throw new AuthError('Real auth not implemented', 'NOT_IMPLEMENTED');
}

/**
 * Sign in an existing user
 */
export async function signIn(input: SignInInput): Promise<AuthResult> {
  if (USE_MOCK) {
    return mockSignIn(input);
  }

  // TODO: Real Amplify implementation
  throw new AuthError('Real auth not implemented', 'NOT_IMPLEMENTED');
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<void> {
  if (USE_MOCK) {
    currentUser = null;
    return;
  }

  // TODO: Real Amplify implementation
  throw new AuthError('Real auth not implemented', 'NOT_IMPLEMENTED');
}

/**
 * Get the currently signed in user
 */
export async function getCurrentUser(): Promise<User | null> {
  if (USE_MOCK) {
    return currentUser;
  }

  // TODO: Real Amplify implementation
  throw new AuthError('Real auth not implemented', 'NOT_IMPLEMENTED');
}

/**
 * Confirm sign up with verification code
 */
export async function confirmSignUp(
  input: ConfirmSignUpInput
): Promise<AuthResult> {
  if (USE_MOCK) {
    return mockConfirmSignUp(input);
  }

  // TODO: Real Amplify implementation
  throw new AuthError('Real auth not implemented', 'NOT_IMPLEMENTED');
}

/**
 * Request password reset
 */
export async function resetPassword(
  input: ResetPasswordInput
): Promise<AuthResult> {
  if (USE_MOCK) {
    return mockResetPassword(input);
  }

  // TODO: Real Amplify implementation
  throw new AuthError('Real auth not implemented', 'NOT_IMPLEMENTED');
}

/**
 * Confirm password reset with code and new password
 */
export async function confirmResetPassword(
  input: ConfirmResetPasswordInput
): Promise<AuthResult> {
  if (USE_MOCK) {
    return mockConfirmResetPassword(input);
  }

  // TODO: Real Amplify implementation
  throw new AuthError('Real auth not implemented', 'NOT_IMPLEMENTED');
}

/**
 * Resend confirmation code
 */
export async function resendConfirmationCode(
  _email: string
): Promise<AuthResult> {
  if (USE_MOCK) {
    // In mock mode, just return success (real implementation would send email)
    return { success: true };
  }

  // TODO: Real Amplify implementation
  throw new AuthError('Real auth not implemented', 'NOT_IMPLEMENTED');
}

// ============== Mock Implementations ==============

async function mockSignUp(input: SignUpInput): Promise<AuthResult> {
  // Simulate network delay
  await delay(500);

  // Check if user already exists
  if (mockUsers.has(input.email)) {
    throw new AuthError(
      'An account with this email already exists',
      'UsernameExistsException'
    );
  }

  // Validate password
  if (input.password.length < 8) {
    throw new AuthError(
      'Password must be at least 8 characters',
      'InvalidPasswordException'
    );
  }

  // Create user (unconfirmed)
  const user: User = {
    id: `user-${Date.now()}`,
    username: input.username,
    email: input.email,
    emailVerified: false,
    createdAt: new Date().toISOString(),
  };

  mockUsers.set(input.email, {
    user,
    password: input.password,
    confirmed: false,
  });

  return {
    success: true,
    needsConfirmation: true,
  };
}

async function mockSignIn(input: SignInInput): Promise<AuthResult> {
  await delay(500);

  const userData = mockUsers.get(input.email);

  if (!userData) {
    throw new AuthError('User does not exist', 'UserNotFoundException');
  }

  if (!userData.confirmed) {
    throw new AuthError('User is not confirmed', 'UserNotConfirmedException');
  }

  if (userData.password !== input.password) {
    throw new AuthError('Incorrect password', 'NotAuthorizedException');
  }

  currentUser = userData.user;

  return {
    success: true,
    user: userData.user,
  };
}

async function mockConfirmSignUp(
  input: ConfirmSignUpInput
): Promise<AuthResult> {
  await delay(500);

  const userData = mockUsers.get(input.email);

  if (!userData) {
    throw new AuthError('User does not exist', 'UserNotFoundException');
  }

  // Mock: accept any 6-digit code
  if (!/^\d{6}$/.test(input.code)) {
    throw new AuthError('Invalid verification code', 'CodeMismatchException');
  }

  userData.confirmed = true;
  userData.user.emailVerified = true;

  return {
    success: true,
    user: userData.user,
  };
}

async function mockResetPassword(
  _input: ResetPasswordInput
): Promise<AuthResult> {
  await delay(500);

  // Don't reveal if user exists for security - always return success
  return { success: true };
}

async function mockConfirmResetPassword(
  input: ConfirmResetPasswordInput
): Promise<AuthResult> {
  await delay(500);

  const userData = mockUsers.get(input.email);

  if (!userData) {
    throw new AuthError('User does not exist', 'UserNotFoundException');
  }

  // Mock: accept any 6-digit code
  if (!/^\d{6}$/.test(input.code)) {
    throw new AuthError('Invalid verification code', 'CodeMismatchException');
  }

  if (input.newPassword.length < 8) {
    throw new AuthError(
      'Password must be at least 8 characters',
      'InvalidPasswordException'
    );
  }

  userData.password = input.newPassword;

  return { success: true };
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Pre-populate a test user for development
if (USE_MOCK) {
  mockUsers.set('test@example.com', {
    user: {
      id: 'user-test',
      username: 'TestUser',
      email: 'test@example.com',
      emailVerified: true,
      createdAt: '2025-01-01T00:00:00.000Z',
    },
    password: 'password123',
    confirmed: true,
  });
}
